import PropTypes from 'prop-types'
import axios from 'axios'
import React from 'react'
import { AsyncPaginate } from 'react-select-async-paginate'
import { FormGroup, InputGroup, InputGroupAddon, InputGroupText, Label } from 'reactstrap'

const defaultPaginationOptions = {
  page: 0,
  pageSize: 50,
}

const baseInputStyles = {
  container: (provided) => {
    return {
      ...provided,
      flex: 1,
    }
  },
}

/**
 * Async Select With Infinite Scrolling Component
 * @param {function} loadOptions - @description loadOptions(search:string, pagination:{ page: number, pageSize: number })  => loadOptions returns { options: { value: any, label: string }[], hasMore: bool }
 */
const AsyncSelect = ({
  label,
  loadOptions,
  isMulti,
  onInputChange,
  formClassName,
  onChange,
  name,
  styles,
  className,
  placeholder,
  loadingMessage,
  noOptionsMessage,
  paginationOptions,
  prependAddon,
  value,
}) => {
  const customStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: '29px',
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: '29px',
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: '5px',
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: '5px',
    }),
    menu: (provided) => ({ ...provided, zIndex: 100000000, color: '#000' }),
  }

  const loadPageOptions = async (searchBy, prevOptions, { page }) => {
    const { options, hasMore } = await loadOptions(searchBy, { page, pageSize: paginationOptions.pageSize })

    return {
      options,
      hasMore,
      additional: {
        page: page + 1,
      },
    }
  }

  return (
    <>
      <FormGroup className={formClassName}>
        {label && <Label>{label}:</Label>}
        <InputGroup style={{ display: 'flex', flexDirection: 'row' }}>
          {prependAddon && (
            <InputGroupAddon addonType='prepend'>
              <InputGroupText style={{ padding: '0 5px' }}>{prependAddon}</InputGroupText>
            </InputGroupAddon>
          )}
          <AsyncPaginate
            cacheOptions
            defaultOptions
            loadOptionsOnMenuOpen
            isClearable
            isMulti={isMulti}
            // Controlled only when the caller passes `value` (an option object / array
            // of them); undefined keeps every existing consumer uncontrolled as before.
            value={value}
            additional={defaultPaginationOptions}
            name={name}
            // value={
            //   isMulti
            //     ? options.filter((option) => {
            //         return defaultValue.includes(option.id) || defaultValue.includes(option.value);
            //       })
            //     : options.find((option) => option.value === defaultValue || option.id === defaultValue) || 0
            // }
            loadOptions={loadPageOptions}
            onChange={onChange}
            onInputChange={onInputChange}
            loadingMessage={() => loadingMessage}
            noOptionsMessage={() => noOptionsMessage}
            placeholder={placeholder}
            styles={{ ...styles, ...baseInputStyles, ...customStyles }}
            className={className}
          />
        </InputGroup>
      </FormGroup>
    </>
  )
}

AsyncSelect.propTypes = {
  label: PropTypes.string,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  onInputChange: PropTypes.func,
  loadOptions: PropTypes.func.isRequired,
  name: PropTypes.string,
  formClassName: PropTypes.string,
  styles: PropTypes.object,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  loadingMessage: PropTypes.string,
  noOptionsMessage: PropTypes.string,
  paginationOptions: PropTypes.shape({ page: PropTypes.number, pageSize: PropTypes.number }),
  prependAddon: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Element)]),
}

AsyncSelect.defaultProps = {
  paginationOptions: defaultPaginationOptions,
  loadingMessage: 'Loading...',
  noOptionsMessage: 'No results found',
  prependAddon: '',
}

const useAsyncSelect = () => {
  const fetchOptions = async (url, options) => {
    const data = await axios.get(url, { params: { ...options } })
    if (data.status === 200) {
      const { options: selectOptions, hasMore } = data.data
      return { options: selectOptions || [], hasMore: hasMore || false }
    } else {
      return {
        options: [],
        hasMore: false,
      }
    }
  }

  return { fetchOptions }
}

export { useAsyncSelect }

export default AsyncSelect
